import React from "react";
import {
  Card,
  CardContent,
  TextField,
  Typography,
  Box,
  Tooltip,
  IconButton,
  Divider,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { MarketValueInputs } from "../types";

interface InputFormProps {
  inputs: MarketValueInputs;
  onInputChange: (field: keyof MarketValueInputs, value: number) => void;
}

export const InputForm: React.FC<InputFormProps> = ({
  inputs,
  onInputChange,
}) => {
  const handleChange =
    (field: keyof MarketValueInputs) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(event.target.value) || 0;
      onInputChange(field, value);
    };

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Market Value Calculator Inputs
        </Typography>

        <Typography variant="subtitle1" color="primary" sx={{ mt: 2, mb: 1 }}>
          Disease-Specific Parameters
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
            mb: 3,
          }}
        >
          <Box>
            <div style={{ display: "flex", alignItems: "center" }}>
              <TextField
                label="Initial Patient Population"
                type="number"
                value={inputs.initialPatientPopulation || ""}
                onChange={handleChange("initialPatientPopulation")}
              />
              <Tooltip title="Total number of existing patients who could benefit from the therapy">
                <IconButton size="small">
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </div>
          </Box>
          <Box>
            <div style={{ display: "flex", alignItems: "center" }}>
              <TextField
                label="New Patients Per Year"
                type="number"
                value={inputs.newPatientsPerYear || ""}
                onChange={handleChange("newPatientsPerYear")}
              />
              <Tooltip title="Expected number of new patients diagnosed annually">
                <IconButton size="small">
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </div>
          </Box>
          <Box>
            <div style={{ display: "flex", alignItems: "center" }}>
              <TextField
                label="Therapeutic Price ($USD)"
                type="number"
                value={inputs.therapeuticPrice || ""}
                onChange={handleChange("therapeuticPrice")}
              />
              <Tooltip title="Expected price per treatment">
                <IconButton size="small">
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </div>
          </Box>
          <Box>
            <div style={{ display: "flex", alignItems: "center" }}>
              <TextField
                label="Economic Cost Per Patient ($USD)"
                type="number"
                value={inputs.economicCostPerPatient || ""}
                onChange={handleChange("economicCostPerPatient")}
              />
              <Tooltip title="Annual economic burden per patient without treatment">
                <IconButton size="small">
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </div>
          </Box>
          <Box>
            <div style={{ display: "flex", alignItems: "center" }}>
              <TextField
                label="Development Cost ($USD)"
                type="number"
                value={inputs.developmentCost || ""}
                onChange={handleChange("developmentCost")}
              />
              <Tooltip title="Expected years of life affected by the disease">
                <IconButton size="small">
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </div>
          </Box>
          <Box>
            <div style={{ display: "flex", alignItems: "center" }}>
              <TextField
                label="Healthcare Cost Per Patient ($USD)"
                type="number"
                value={inputs.healthcareCostPerPatient || ""}
                onChange={handleChange("healthcareCostPerPatient")}
              />
              <Tooltip title="Expected cost of healthcare per patient without a therapeutic.">
                <IconButton size="small">
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </div>
          </Box>
          {/* TODO: Determine if we want to bring this back at some point */}
          {/* <Box>
            <div style={{ display: "flex", alignItems: "center" }}>
              <TextField
                label="Years of Life Impact"
                type="number"
                value={inputs.yearsOfLife || ""}
                onChange={handleChange("yearsOfLife")}
              />
              <Tooltip title="Expected years of life affected by the disease">
                <IconButton size="small">
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </div>
          </Box> */}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" color="primary" sx={{ mt: 2, mb: 1 }}>
          Calculation Parameters
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
          }}
        >
          <Box>
            <div style={{ display: "flex", alignItems: "center" }}>
              <TextField
                label="Projection Years"
                type="number"
                value={inputs.projectionYears || ""}
                onChange={handleChange("projectionYears")}
              />
              <Tooltip title="Number of years to project market value">
                <IconButton size="small">
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </div>
          </Box>
          <Box>
            <div style={{ display: "flex", alignItems: "center" }}>
              <TextField
                label="Market Penetration Rate (%)"
                type="number"
                // Deprecated, but it still works. We can move to using base-ui as recommended later if we so decide.
                inputProps={{ max: 100, min: 0 }}
                value={inputs.marketPenetrationRate || ""}
                onChange={handleChange("marketPenetrationRate")}
              />
              <Tooltip title="The percentage of the target market that is using the therapeutic each year">
                <IconButton size="small">
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </div>
          </Box>
          <Box>
            <div style={{ display: "flex", alignItems: "center" }}>
              <TextField
                label="Probability of Approval (%)"
                type="number"
                // Deprecated, but it still works. We can move to using base-ui as recommended later if we so decide.
                inputProps={{ max: 100, min: 0 }}
                value={inputs.approvalProbability || ""}
                onChange={handleChange("approvalProbability")}
              />
              <Tooltip title="The percentage chance that the therapeutic will be approved by the FDA">
                <IconButton size="small">
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </div>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
